'use client';

import React from 'react';
import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';

const radarData = [
  { dimension: 'AI Readiness', current: 67, benchmark: 55 },
  { dimension: 'Data Maturity', current: 54, benchmark: 61 },
  { dimension: 'Cyber', current: 71, benchmark: 58 },
  { dimension: 'Automation', current: 48, benchmark: 52 },
  { dimension: 'Leadership', current: 82, benchmark: 64 },
];

interface TooltipPayload {
  name: string;
  value: number;
  color: string;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayload[];
  label?: string;
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div
      className="rounded-lg p-3 text-xs"
      style={{
        background: 'rgba(15,30,53,0.95)',
        border: '1px solid rgba(201,168,76,0.3)',
        boxShadow: '0 8px 24px rgba(5,13,26,0.6)',
      }}
    >
      <p className="font-700 text-foreground mb-1.5">{label}</p>
      {payload.map((entry, idx) => (
        <div key={`tooltip-entry-${idx}`} className="flex items-center gap-2 mb-0.5">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: entry.color }} />
          <span className="text-muted-foreground">{entry.name}:</span>
          <span className="font-700 tabular-nums" style={{ color: entry.color }}>
            {entry.value}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function CapabilityRadarChart() {
  return (
    <ResponsiveContainer width="100%" height={220}>
      <RadarChart data={radarData} margin={{ top: 8, right: 20, bottom: 8, left: 20 }}>
        <defs>
          <linearGradient id="radarGradientCurrent" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--gold-400)" stopOpacity={0.35} />
            <stop offset="100%" stopColor="var(--gold-600)" stopOpacity={0.1} />
          </linearGradient>
          <linearGradient id="radarGradientBenchmark" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--teal-500)" stopOpacity={0.25} />
            <stop offset="100%" stopColor="var(--teal-700)" stopOpacity={0.05} />
          </linearGradient>
        </defs>
        <PolarGrid stroke="rgba(30,58,90,0.5)" strokeDasharray="3 3" />
        <PolarAngleAxis
          dataKey="dimension"
          tick={{ fill: 'var(--muted-foreground)', fontSize: 10, fontWeight: 500 }}
        />
        <Radar
          name="Industry Avg"
          dataKey="benchmark"
          stroke="var(--teal-500)"
          fill="url(#radarGradientBenchmark)"
          strokeWidth={1.5}
          strokeDasharray="4 3"
          dot={false}
        />
        <Radar
          name="Current"
          dataKey="current"
          stroke="var(--gold-500)"
          fill="url(#radarGradientCurrent)"
          strokeWidth={2}
          dot={{ fill: 'var(--gold-500)', r: 3, strokeWidth: 0 }}
        />
        <Tooltip content={<CustomTooltip />} />
      </RadarChart>
    </ResponsiveContainer>
  );
}